import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALL_SUBMARKETS, ON_SUBMARKET_CHANGED } from '../../graphql/submarkets';
import SubmarketsList from './SubmarketsList';
import CreateSubmarketRow from './CreateSubmarketRow';
import { showInfoNotification } from '../../actions/notifications';
import Paginator from '../Paginator';
import {
    parseIncludeDeactivated,
    parsePaging,
    parseQuery,
    parseSorting,
} from '../../utils/queryString';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import SubscriptionActions from '../../utils/SubscriptionActions';
import Constants from '../../utils/Constants';
import Error from '../errors/Error';
import CheckboxField from '../fields/CheckboxField';
import Search from '../Search';
import { getValueMapping } from './SubmarketRow';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';

export function createNotificationText(t, action, name, code) {
    let notification;
    switch (action) {
        case SubscriptionActions.SUBMARKET_CREATED:
            notification = t('submarkets.notification.submarket-create-thirdparty', { name, code });
            break;
        case SubscriptionActions.SUBMARKET_UPDATED:
            notification = t('submarkets.notification.submarket-update-thirdparty', { name, code });
            break;
        default:
            notification = t('submarkets.notification.submarkets-modified-thirdparty');
    }
    return notification;
}

function Submarkets() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'code',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const query = parseQuery(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        query,
        includeDeactivated,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
        previousData,
    } = useExtendedQuery(ALL_SUBMARKETS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onSearch = (newQuery) => setRefetchProps({
        ...refetchProps,
        query: newQuery,
        page: 0,
    });
    const onFilter = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    useExtendedSubscription(ON_SUBMARKET_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'submarketChanged',
        onChangeCallback: ({ action, model: { code, name } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.SUBMARKET_EDIT])) {
                const notification = createNotificationText(t, action, name, code);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let searchMatches = 0;
    if (loading) {
        result = <Spinner className="submarkets-loading" />;
    } else if (error) {
        result = <Error className="submarkets-error" error={error} />;
    } else {
        const searchResult = data === undefined ? previousData : data;
        searchMatches = searchResult.allSubmarkets.totalCount;
        result = (
            <Paginator
                totalCount={searchResult.allSubmarkets.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <SubmarketsList
                    submarkets={searchResult.allSubmarkets.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="globe" />&nbsp;{t('title.submarkets')}</h4>
            <CreateSubmarketRow />
            <div className="search-and-filter submarkets">
                <Search
                    placeholder={t('submarkets.placeholder.search')}
                    query={refetchProps.query}
                    onSearch={onSearch}
                    matches={searchMatches}
                />
                <div className="filter">
                    <CheckboxField
                        label={t('submarkets.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onFilter}
                        className="submarkets--search-and-filter--include-deactivated"
                        inputId="submarkets--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Submarkets;
